import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { EvalService } from "@/gen/omnios/v1/eval_connect.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Lock, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/eval/datasets")({
	component: EvalDatasets,
});

const evalClient = createClient(EvalService, transport);

function EvalDatasets() {
	const { t } = useTranslation("eval");
	const qc = useQueryClient();
	const [createOpen, setCreateOpen] = useState(false);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [kind, setKind] = useState("golden_match");
	const [search, setSearch] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["eval", "datasets"],
		queryFn: () => evalClient.listEvalDatasets({ pageSize: 100 }),
	});

	const createMut = useMutation({
		mutationFn: () => evalClient.createEvalDataset({ name, description, kind }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["eval", "datasets"] });
			setCreateOpen(false);
			setName("");
			setDescription("");
			toast.success(t("dataset_created"));
		},
	});

	const filtered = data?.datasets?.filter(
		(ds) => !search || ds.name.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-4">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={t("dataset_search_placeholder")}
						className="pl-9"
					/>
				</div>
				<Dialog open={createOpen} onOpenChange={setCreateOpen}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="mr-1 h-4 w-4" />
							{t("dataset_create")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t("dataset_create_title")}</DialogTitle>
						</DialogHeader>
						<div className="space-y-4 pt-2">
							<div className="space-y-2">
								<Label>{t("dataset_name")}</Label>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder={t("dataset_name_placeholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("dataset_description")}</Label>
								<Input
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder={t("dataset_description_placeholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("dataset_kind")}</Label>
								<Select value={kind} onValueChange={setKind}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="golden_match">
											{t("dataset_kind_golden_match")}
										</SelectItem>
										<SelectItem value="query">
											{t("dataset_kind_query")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<Button
								className="w-full"
								onClick={() => createMut.mutate()}
								disabled={!name || createMut.isPending}
							>
								{t("dataset_create")}
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</div>

			{isLoading ? (
				<Skeleton className="h-48" />
			) : !filtered?.length ? (
				<Card>
					<CardContent className="py-8 text-center text-muted-foreground">
						{t("dataset_empty")}
					</CardContent>
				</Card>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("dataset_name")}</TableHead>
								<TableHead>{t("dataset_kind")}</TableHead>
								<TableHead>{t("col_version")}</TableHead>
								<TableHead>{t("dataset_rows")}</TableHead>
								<TableHead>{t("col_status")}</TableHead>
								<TableHead>{t("col_date")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((ds) => (
								<TableRow
									key={ds.id}
									className="cursor-pointer hover:bg-muted/50"
								>
									<TableCell>
										<Link
											to="/admin/eval/datasets/$id"
											params={{ id: ds.id }}
											className="font-medium text-primary hover:underline"
										>
											{ds.name}
										</Link>
										{ds.description && (
											<p className="text-xs text-muted-foreground truncate max-w-[300px]">
												{ds.description}
											</p>
										)}
									</TableCell>
									<TableCell>
										<Badge variant="outline" className="text-xs">
											{ds.kind}
										</Badge>
									</TableCell>
									<TableCell className="font-mono text-xs">
										v{ds.version}
									</TableCell>
									<TableCell>{ds.rowCount}</TableCell>
									<TableCell>
										{ds.frozenAt ? (
											<Badge variant="secondary" className="text-xs">
												<Lock className="mr-1 h-3 w-3" />
												{t("dataset_frozen_badge")}
											</Badge>
										) : (
											<Badge variant="outline" className="text-xs">
												{t("dataset_active_badge")}
											</Badge>
										)}
									</TableCell>
									<TableCell className="text-xs text-muted-foreground">
										{ds.createdAt
											? new Date(
													Number(ds.createdAt.seconds) * 1000,
												).toLocaleDateString()
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
